import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Search, Trash2, Star, X, Link2, Loader2, Package, Save, Layers, ChevronDown, Check } from "lucide-react";
import {
  fetchIngredients, createIngredient, updateIngredient, deleteIngredient,
  attachIngredientSource, detachIngredientSource, updateIngredientSourcePack, resolveUnitChange,
  fetchUnmappedSupplierProducts, fetchSuppliers, fetchIngredientHistory,
  type Ingredient, type IngredientPricePoint, type IngredientSource, type UnmappedSupplierProduct,
  type PackUnit, type PendingUnit,
} from "../../services/apiService";
import { useBusiness } from "../../contexts/BusinessContext";
import type { Supplier } from "../../types";

const NAVY = "#2a2420";
const GREEN = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const WARN = "#c2412e";
const WARN_WEAK = "rgba(194,65,46,0.10)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";

const UNIT_OPTIONS = ["kg", "g", "L", "mL", "ea", "piece", "punnet", "portion", "serving", "batch", "oz", "lb", "tsp", "tbsp", "cup", "fl oz"];
const SUPPLIER_COLORS = ["#607840", "#c2412e", "#3c7d57", "#b8860b", "#5b7fa6", "#8a5a83", "#2f8f8f"];
const SUPPLIER_TABLE_COLUMNS = "minmax(120px,0.8fr) minmax(200px,1fr) minmax(120px,0.7fr) 190px 100px 26px";

const DRAFT_ID = "__draft__";

function newDraftIngredient(category: string | null): Ingredient {
  return {
    id: DRAFT_ID,
    name: "",
    canonicalUnit: "kg",
    category,
    confirmed: false,
    pricePolicy: "preferred",
    preferredSupplierProductId: null,
    currentPrice: null,
    previousPrice: null,
    currentSupplierId: null,
    manualPrice: null,
    sourceCount: 0,
    sources: [],
  };
}

function fmtPrice(p: number | null): string {
  if (p == null || !Number.isFinite(p)) return "—";
  return `$${p.toFixed(p < 1 ? 3 : 2)}`;
}

function matchScore(query: string, text: string): number {
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  const t = text.toLowerCase();
  let s = 0;
  for (const tok of q.split(/\s+/)) if (tok.length > 1 && t.includes(tok)) s += 1;
  return s;
}

// ── Price history ─────────────────────────────────────────────────────────────────
function PriceHistory({ ingredientId, businessId, unit, supplierName }: {
  ingredientId: string; businessId?: string; unit: string; supplierName: (id: string) => string;
}) {
  const { data, isLoading } = useQuery<IngredientPricePoint[]>({
    queryKey: ["ingredient-history", ingredientId, businessId],
    queryFn: () => fetchIngredientHistory(ingredientId, businessId),
    enabled: Boolean(businessId),
  });
  if (isLoading) return <p className="text-xs italic" style={{ color: MUTED }}>Loading price history…</p>;
  const points: IngredientPricePoint[] = data ?? [];
  if (points.length === 0) return <p className="text-xs italic" style={{ color: MUTED }}>No price history yet — map a supplier item; prices appear as invoices arrive.</p>;
  const supplierIds = [...new Set(points.map((p) => p.supplierId))];
  const colorFor = (id: string) => SUPPLIER_COLORS[Math.max(0, supplierIds.indexOf(id)) % SUPPLIER_COLORS.length];

  // Flagged (unit-change / price-jump) points are quarantined: they must not join the
  // connecting polyline or stretch the y-axis (one $60 tray would flatten a $5–7 series).
  // Scale the axis from trusted points only; render flagged ones as detached grey circles.
  const trusted = points.filter((p) => !p.flagged && Number.isFinite(p.price)).map((p) => p.price);
  const scaleBase = trusted.length ? trusted : points.map((p) => p.price).filter(Number.isFinite);
  const lo = scaleBase.length ? Math.min(...scaleBase) : 0;
  const hi = scaleBase.length ? Math.max(...scaleBase) : 1;
  const pad = (hi - lo) * 0.1 || hi * 0.1 || 1;
  const domainMin = Math.max(0, lo - pad);
  const domainMax = hi + pad;
  const clamp = (v: number) => Math.min(domainMax, Math.max(domainMin, v));
  const chartData = points.map((p) => ({
    date: p.date,
    supplierId: p.supplierId,
    flagged: p.flagged === true,
    rawPrice: p.price,
    price: p.flagged ? null : p.price,                    // polyline + axis: trusted only
    flaggedPrice: p.flagged ? clamp(p.price) : null,      // detached grey marker
  }));
  return (
    <div>
      <ResponsiveContainer width="100%" height={170}>
        <LineChart data={chartData} margin={{ top: 6, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 9, fill: MUTED }} tickFormatter={(d) => String(d).slice(5)} />
          <YAxis tick={{ fontSize: 9, fill: MUTED }} width={44} domain={[domainMin, domainMax]} allowDataOverflow tickFormatter={(v) => `$${Number(v).toFixed(1)}`} />
          <Tooltip
            formatter={(value: number, _n: string, p: { payload?: { supplierId?: string; rawPrice?: number; flagged?: boolean } }) => {
              const price = p?.payload?.rawPrice ?? value;
              const name = supplierName(p?.payload?.supplierId ?? "");
              return [`$${Number(price).toFixed(2)}/${unit}`, p?.payload?.flagged ? `${name} · flagged` : name];
            }}
            labelFormatter={(l) => String(l)}
            contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${LINE}` }}
          />
          <Line
            type="monotone" dataKey="price" stroke={MUTED} strokeWidth={1.5} connectNulls isAnimationActive={false} activeDot={{ r: 5 }}
            dot={(props: { cx?: number; cy?: number; index?: number; payload?: { supplierId?: string } }) => (
              <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill={colorFor(props.payload?.supplierId ?? "")} stroke="#fff" strokeWidth={1} />
            )}
          />
          <Line
            type="monotone" dataKey="flaggedPrice" stroke="transparent" strokeWidth={0} connectNulls={false} isAnimationActive={false} activeDot={false}
            dot={(props: { cx?: number; cy?: number; index?: number; value?: number | null }) => (
              props.value == null
                ? <g key={props.index} />
                : <circle key={props.index} cx={props.cx} cy={props.cy} r={4} fill="none" stroke={MUTED} strokeWidth={1.5} strokeDasharray="2 1.5" />
            )}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-3 mt-1.5">
        {supplierIds.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 text-[10px]" style={{ color: NAVY }}>
            <span style={{ width: 8, height: 8, borderRadius: 8, backgroundColor: colorFor(id), display: "inline-block" }} />
            {supplierName(id)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Editable pack / conversion cell ───────────────────────────────────────────────
type PackSave = (packQuantity: number | null, packUnit: string | null, unitKey?: string) => void;
type PackResolve = (unitKey: string, packQuantity: number, packUnit: string) => void;

// A newly-detected unit awaiting its conversion rate. Auto-appears in the pack cell
// (amber) with the rate pre-filled from the last trusted price; the line stays
// quarantined until the user confirms. Confirming maps it and clears the quarantine.
// The conversion target is always the ingredient's unit (shown as plain text).
function PendingUnitRow({ pending, ingredientUnit, currentPrice, onResolve }: {
  pending: PendingUnit; ingredientUnit: string; currentPrice: number | null; onResolve: PackResolve;
}) {
  const suggested = (() => {
    const cp = currentPrice && currentPrice > 0 ? currentPrice : null;
    const lp = pending.latestUnitPrice;
    if (cp && lp && lp > 0) return String(Math.max(1, Math.round(lp / cp)));
    return "";
  })();
  const [qty, setQty] = useState(suggested);
  const confirm = () => {
    const n = qty.trim() === "" ? null : Number(qty.trim());
    if (n === null || !Number.isFinite(n) || n <= 0) return;
    onResolve(pending.unitKey, n, ingredientUnit);
  };
  return (
    <span className="flex items-center justify-center gap-1.5" style={{ minHeight: 28 }} title="New unit detected — set its rate to confirm">
      <span
        className="tabular-nums"
        style={{ fontSize: 10.5, fontWeight: 700, color: "#8a6508", backgroundColor: "rgba(184,134,11,0.16)", borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap", minWidth: 62, textAlign: "center", flexShrink: 0 }}
      >
        1&nbsp;{pending.unitKey}
      </span>
      <span style={{ fontSize: 11, color: MUTED }}>=</span>
      <input
        value={qty} onChange={(e) => setQty(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") confirm(); }}
        placeholder="?" inputMode="decimal"
        className="text-xs rounded-md px-1.5 py-1 outline-none tabular-nums"
        style={{ width: 44, border: "1px solid #b8860b", color: NAVY, textAlign: "center" }}
      />
      <span style={{ fontSize: 11, color: NAVY, fontWeight: 600 }}>{ingredientUnit}</span>
      <button type="button" onClick={confirm} title="Confirm new unit" style={{ color: GREEN, lineHeight: 0, marginLeft: 2 }}><Check size={14} /></button>
    </span>
  );
}

// One mapped sell-unit: "1 tray = [10] punnet". Target unit is the ingredient's unit
// (fixed text); the user edits only the quantity. Auto-saves on blur.
function PackUnitRow({ unit, ingredientUnit, onSave }: {
  unit: PackUnit; ingredientUnit: string; onSave: PackSave;
}) {
  const [qty, setQty] = useState(unit.packQuantity != null ? String(unit.packQuantity) : "");
  useEffect(() => {
    setQty(unit.packQuantity != null ? String(unit.packQuantity) : "");
  }, [unit.unitKey, unit.packQuantity]);
  const commit = () => {
    const t = qty.trim();
    const n = t === "" ? null : Number(t);
    if (n !== null && (!Number.isFinite(n) || n <= 0)) return;
    onSave(n, ingredientUnit, unit.unitKey);
  };
  return (
    <span className="group flex items-center justify-center gap-1.5" style={{ minHeight: 28 }}>
      <span
        className="tabular-nums"
        style={{ fontSize: 10.5, fontWeight: 700, color: NAVY, backgroundColor: `${NAVY}0d`, borderRadius: 5, padding: "2px 6px", whiteSpace: "nowrap", minWidth: 62, textAlign: "center", flexShrink: 0 }}
        title={`One ${unit.unitKey}`}
      >
        1&nbsp;{unit.unitKey}
      </span>
      <span style={{ fontSize: 11, color: MUTED }}>=</span>
      <input
        value={qty} onChange={(e) => setQty(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder="—" inputMode="decimal"
        className="text-xs rounded-md px-1.5 py-1 outline-none tabular-nums"
        style={{ width: 44, border: `1px solid ${LINE}`, color: NAVY, textAlign: "center" }}
      />
      <span style={{ fontSize: 11, color: NAVY, fontWeight: 600 }}>{ingredientUnit}</span>
      <button
        type="button" title="Remove unit"
        onClick={() => onSave(null, unit.packUnit, unit.unitKey)}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ color: MUTED, lineHeight: 0, marginLeft: 2 }}
      >
        <X size={11} />
      </button>
    </span>
  );
}

// Unit-aware pack editor: pending detected units (need a rate) + a row per mapped
// sell-unit. No manual "add" — sell-units come from invoice detection.
function PackUnitsCell({ units, pendingUnits, ingredientUnit, currentPrice, onSave, onResolve }: {
  units: PackUnit[]; pendingUnits: PendingUnit[]; ingredientUnit: string;
  currentPrice: number | null; onSave: PackSave; onResolve: PackResolve;
}) {
  return (
    <span className="flex flex-col gap-1 items-center">
      {pendingUnits.map((p) => (
        <PendingUnitRow key={`pending-${p.unitKey}`} pending={p} ingredientUnit={ingredientUnit} currentPrice={currentPrice} onResolve={onResolve} />
      ))}
      {units.map((u) => (
        <PackUnitRow key={u.unitKey} unit={u} ingredientUnit={ingredientUnit} onSave={onSave} />
      ))}
    </span>
  );
}

// Legacy single-pack editor (unchanged behavior for SKUs with no per-unit mappings).
function LegacyPackCell({ source, ingredientUnit, onSave }: {
  source: IngredientSource;
  ingredientUnit: string;
  onSave: PackSave;
}) {
  const [qty, setQty] = useState(source.packQuantity != null ? String(source.packQuantity) : "");
  const [unit, setUnit] = useState(source.packUnit ?? ingredientUnit);
  useEffect(() => {
    setQty(source.packQuantity != null ? String(source.packQuantity) : "");
    setUnit(source.packUnit ?? ingredientUnit);
  }, [source.supplierProductId, source.packQuantity, source.packUnit, ingredientUnit]);
  const commit = (q: string, u: string) => {
    const trimmed = q.trim();
    const n = trimmed === "" ? null : Number(trimmed);
    if (n !== null && (!Number.isFinite(n) || n <= 0)) return;
    onSave(n, u);
  };
  return (
    <span className="flex items-center justify-center gap-1" style={{ minHeight: 28 }}>
      <input
        value={qty}
        onChange={(e) => setQty(e.target.value)}
        onBlur={() => commit(qty, unit)}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder="—"
        inputMode="decimal"
        className="text-xs rounded-md px-1.5 py-1 outline-none tabular-nums"
        style={{ width: 44, border: `1px solid ${LINE}`, color: NAVY, textAlign: "center" }}
      />
      <select
        value={unit}
        onChange={(e) => { setUnit(e.target.value); commit(qty, e.target.value); }}
        className="text-xs rounded-md px-1 py-1 outline-none"
        style={{ width: 62, border: `1px solid ${LINE}`, color: NAVY, backgroundColor: "white" }}
      >
        {UNIT_OPTIONS.includes(unit) ? null : <option value={unit}>{unit}</option>}
        {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
    </span>
  );
}

// Selector: show every sell-unit as a row (same "1 unit = rate ingredient-unit"
// format) when the SKU has any unit rows or a pending detected unit. Only SKUs with
// no unit rows at all fall back to the simple legacy pack editor.
function PackCell({ source, ingredientUnit, currentPrice, onSave, onResolve }: {
  source: IngredientSource;
  ingredientUnit: string;
  currentPrice: number | null;
  onSave: PackSave;
  onResolve: PackResolve;
}) {
  const units = source.packUnits || [];
  const pending = source.pendingUnits || [];
  if (units.length === 0 && pending.length === 0) {
    return <LegacyPackCell source={source} ingredientUnit={ingredientUnit} onSave={onSave} />;
  }
  return <PackUnitsCell units={units} pendingUnits={pending} ingredientUnit={ingredientUnit} currentPrice={currentPrice} onSave={onSave} onResolve={onResolve} />;
}

// ── Right detail: base ingredient editor ──────────────────────────────────────────
function BaseIngredientEditor({ ingredient, businessId, unmapped, supplierName, groups, mutators, isDraft }: {
  ingredient: Ingredient;
  businessId?: string;
  unmapped: UnmappedSupplierProduct[];
  supplierName: (id: string) => string;
  groups: string[];
  isDraft?: boolean;
  mutators: {
    update: (v: { id: string; updates: Parameters<typeof updateIngredient>[1] }) => void;
    remove: (id: string) => void;
    attach: (v: { ingredientId: string; supplierProductId: string }) => void;
    detach: (v: { ingredientId: string; supplierProductId: string }) => void;
    updatePack: (v: { ingredientId: string; supplierProductId: string; packQuantity: number | null; packUnit: string | null; unitKey?: string }) => void;
    resolveUnit: (v: { ingredientId: string; supplierProductId: string; unitKey: string; packQuantity: number; packUnit: string }) => void;
    attachPending: boolean;
    saveDraft: (data: { name: string; canonicalUnit: string; category: string | null; manualPrice: number | null }) => void;
    saveDraftPending: boolean;
    cancelDraft: () => void;
  };
}) {
  const [name, setName] = useState(ingredient.name);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addQuery, setAddQuery] = useState("");
  const [manualPriceInput, setManualPriceInput] = useState(
    ingredient.manualPrice != null ? String(ingredient.manualPrice) : ""
  );
  const [draftUnit, setDraftUnit] = useState(ingredient.canonicalUnit);
  const [draftCategory, setDraftCategory] = useState<string | null>(ingredient.category ?? null);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(ingredient.name);
    setConfirmDelete(false);
    setAdding(false);
    setAddQuery("");
    setManualPriceInput(ingredient.manualPrice != null ? String(ingredient.manualPrice) : "");
    setDraftUnit(ingredient.canonicalUnit);
    setDraftCategory(ingredient.category ?? null);
  }, [ingredient.id]);

  useEffect(() => {
    if (!isDraft) setManualPriceInput(ingredient.manualPrice != null ? String(ingredient.manualPrice) : "");
  }, [ingredient.manualPrice, isDraft]);

  useEffect(() => { if (isDraft) nameRef.current?.focus(); }, [ingredient.id]);

  const commitName = () => {
    if (isDraft) return;
    const n = name.trim();
    if (n && n !== ingredient.name) mutators.update({ id: ingredient.id, updates: { name: n } });
  };

  const commitManualPrice = () => {
    if (isDraft) return; // draft: value read at save time from manualPriceInput state
    const trimmed = manualPriceInput.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) return;
    if (parsed === ingredient.manualPrice) return;
    mutators.update({ id: ingredient.id, updates: { manualPrice: parsed } });
  };

  const hasInvoicePrice = ingredient.sources.some((s) => s.latestPrice != null);
  const activeUnit = isDraft ? draftUnit : ingredient.canonicalUnit;
  const activeCategory = isDraft ? draftCategory : (ingredient.category ?? null);

  // In draft mode, filter out sources already staged locally so they don't appear as suggestions
  const locallyAttached = useMemo(
    () => isDraft ? new Set(ingredient.sources.map((s) => s.supplierProductId)) : new Set<string>(),
    [isDraft, ingredient.sources],
  );
  const availableUnmapped = useMemo(
    () => isDraft ? unmapped.filter((u) => !locallyAttached.has(u.supplierProductId)) : unmapped,
    [isDraft, unmapped, locallyAttached],
  );

  const suggestions = useMemo(() => {
    const key = addQuery.trim() || ingredient.name;
    const typed = addQuery.trim().toLowerCase();
    return availableUnmapped
      .map((u) => ({ u, sc: matchScore(key, `${u.description ?? ""} ${u.productCode} ${u.supplierName ?? ""}`) }))
      .filter((x) => typed
        ? `${x.u.description ?? ""} ${x.u.productCode} ${x.u.supplierName ?? ""}`.toLowerCase().includes(typed)
        : x.sc > 0)
      .sort((a, b) => b.sc - a.sc || (a.u.description ?? "").localeCompare(b.u.description ?? ""))
      .slice(0, 10);
  }, [availableUnmapped, addQuery, ingredient.name]);

  const handleSave = () => {
    const trimmed = manualPriceInput.trim();
    const manualPrice = trimmed === "" ? null : Number(trimmed);
    mutators.saveDraft({
      name: name.trim() || "New ingredient",
      canonicalUnit: draftUnit,
      category: draftCategory,
      manualPrice: (manualPrice !== null && Number.isFinite(manualPrice) && manualPrice >= 0) ? manualPrice : null,
    });
  };

  return (
    <div className="p-6" style={{ overflowY: "auto", flex: 1 }}>
      {/* Name row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 20 }}>
        <input
          ref={nameRef}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
          placeholder="Ingredient name…"
          style={{ flex: 1, minWidth: 0, fontSize: 20, fontWeight: 800, outline: "none", background: "transparent", paddingBottom: 4, color: NAVY, borderBottom: `2px solid ${NAVY}18`, borderTop: "none", borderLeft: "none", borderRight: "none" }}
        />
        {isDraft ? (
          <>
            {/* data-menu-costing-action opts this button out of the global transparent !important rule */}
            <button
              data-menu-costing-action
              type="button"
              disabled={mutators.saveDraftPending}
              onClick={handleSave}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 16px", borderRadius: 8, backgroundColor: GREEN, color: "#fff", fontSize: 13, fontWeight: 700, cursor: mutators.saveDraftPending ? "not-allowed" : "pointer", opacity: mutators.saveDraftPending ? 0.6 : 1, flexShrink: 0, border: "none" }}
            >
              {mutators.saveDraftPending ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save
            </button>
            <button
              data-menu-costing-action
              type="button"
              onClick={mutators.cancelDraft}
              title="Discard"
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 8, borderRadius: 8, backgroundColor: `${NAVY}09`, color: MUTED, cursor: "pointer", flexShrink: 0, border: "none" }}
            >
              <X size={14} />
            </button>
          </>
        ) : (
          <button
            data-menu-costing-action
            type="button"
            onClick={() => setConfirmDelete((v) => !v)}
            style={{ display: "inline-flex", padding: 8, borderRadius: 8, backgroundColor: WARN_WEAK, color: WARN, cursor: "pointer", flexShrink: 0, border: "none" }}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {confirmDelete && (
        <div className="mb-5 flex items-center gap-3 rounded-lg px-4 py-3" style={{ backgroundColor: WARN_WEAK, border: `1px solid ${WARN}33` }}>
          <p className="text-xs flex-1" style={{ color: WARN }}>Delete "{ingredient.name || "this ingredient"}"? Its supplier items become unmapped (not deleted).</p>
          <button data-menu-costing-action type="button" onClick={() => mutators.remove(ingredient.id)} style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "5px 12px", backgroundColor: WARN, color: "#fff", border: "none", cursor: "pointer" }}>Delete</button>
          <button data-menu-costing-action type="button" onClick={() => setConfirmDelete(false)} style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "5px 12px", backgroundColor: `${NAVY}0d`, color: NAVY, border: "none", cursor: "pointer" }}>Cancel</button>
        </div>
      )}

      {/* Core controls */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        {!isDraft && (
          <div className="rounded-xl px-5 py-3" style={{ flex: "0 0 180px", backgroundColor: RAISED, border: `1px solid ${LINE}` }}>
            <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: NAVY, opacity: 0.45 }}>Current price</p>
            <p className="text-2xl font-extrabold" style={{ color: GREEN }}>{fmtPrice(ingredient.currentPrice)}<span className="text-sm font-bold" style={{ color: MUTED }}> /{ingredient.canonicalUnit}</span></p>
          </div>
        )}

        {(groups.length > 0 || isDraft) && (
          <div style={{ flex: "1 1 220px", minWidth: 220 }}>
            <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5" style={{ color: NAVY, opacity: 0.45 }}>Group</label>
            <select
              value={activeCategory ?? ""}
              onChange={(e) => {
                if (isDraft) setDraftCategory(e.target.value || null);
                else mutators.update({ id: ingredient.id, updates: { category: e.target.value || null } });
              }}
              className="rounded-lg px-3 py-2 text-sm outline-none"
              style={{ width: "100%", height: 40, border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: "white" }}
            >
              <option value="">— No group —</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
              {activeCategory && !groups.includes(activeCategory) && (
                <option value={activeCategory}>{activeCategory}</option>
              )}
            </select>
          </div>
        )}

        <div style={{ flex: "0 0 130px" }}>
          <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5" style={{ color: NAVY, opacity: 0.45 }}>Priced per</label>
          <select
            value={activeUnit}
            onChange={(e) => {
              if (isDraft) setDraftUnit(e.target.value);
              else mutators.update({ id: ingredient.id, updates: { canonicalUnit: e.target.value } });
            }}
            className="rounded-lg px-3 py-2 text-sm font-semibold outline-none"
            style={{ width: "100%", height: 40, border: `1.5px solid ${NAVY}24`, color: NAVY, backgroundColor: "white" }}
          >
            {UNIT_OPTIONS.includes(activeUnit) ? null : <option value={activeUnit}>{activeUnit}</option>}
            {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {!hasInvoicePrice && (
          <div style={{ flex: "1 1 260px", minWidth: 260 }}>
            <label className="text-[10px] uppercase tracking-wide font-semibold block mb-1.5" style={{ color: NAVY, opacity: 0.45 }}>Manual price override</label>
            <div className="flex items-center gap-3">
              <div className="flex items-center rounded-lg overflow-hidden" style={{ height: 40, border: `1.5px solid ${NAVY}24` }}>
                <span className="px-2.5 py-2 text-xs font-bold" style={{ alignSelf: "stretch", display: "inline-flex", alignItems: "center", color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}05` }}>$</span>
                <input
                  type="number" min="0" step="0.01"
                  value={manualPriceInput}
                  onChange={(e) => setManualPriceInput(e.target.value)}
                  onBlur={commitManualPrice}
                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                  placeholder="—"
                  className="w-24 px-2 py-2 text-sm font-semibold outline-none"
                  style={{ color: NAVY, backgroundColor: "white" }}
                />
                <span className="px-2.5 py-2 text-xs font-bold" style={{ alignSelf: "stretch", display: "inline-flex", alignItems: "center", color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}05` }}>/{activeUnit}</span>
              </div>
              {!isDraft && ingredient.manualPrice != null && (
                <button
                  data-menu-costing-action
                  type="button"
                  onClick={() => { setManualPriceInput(""); mutators.update({ id: ingredient.id, updates: { manualPrice: null } }); }}
                  style={{ fontSize: 11, fontWeight: 600, color: WARN, background: "none", border: "none", cursor: "pointer" }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Pending unit changes — a heads-up; the unit itself is waiting in the Pack column
          (amber) for its conversion rate. Quarantined until confirmed. */}
      {!isDraft && ingredient.sources.flatMap((s) => s.pendingUnits.map((p) => ({ source: s, pending: p }))).map(({ source, pending }) => {
        const cap = (u: string | null) => (u ? u.charAt(0).toUpperCase() + u.slice(1) : "—");
        return (
          <div key={`${source.supplierProductId}:${pending.unitKey}`} className="mb-3 flex items-center gap-3 rounded-lg px-4 py-2.5" style={{ backgroundColor: "rgba(184,134,11,0.10)", border: "1px solid rgba(184,134,11,0.33)" }}>
            <span className="text-xs flex-1" style={{ color: "#8a6508" }}>
              <b>{supplierName(source.supplierId)}</b> now sells this by the <b>{cap(pending.unitKey)}</b> (was {cap(pending.prevUnitKey)}) — set how many {ingredient.canonicalUnit} are in one {pending.unitKey} in the Pack column to confirm.
            </span>
          </div>
        );
      })}

      {/* Suppliers */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.55 }}>
          Suppliers ({ingredient.sources.length}){isDraft && ingredient.sources.length > 0 && <span className="ml-1.5 normal-case font-normal" style={{ opacity: 0.6 }}>— staged, saved with ingredient</span>}
        </p>
        <button type="button" onClick={() => { setAdding((v) => !v); setAddQuery(""); }} className="inline-flex items-center gap-1.5 text-xs font-bold" style={{ color: GREEN }}>
          <Plus size={13} /> Add
        </button>
      </div>

      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${LINE}` }}>
        <div className="grid items-center px-3 py-2 text-[10px] font-bold uppercase tracking-wide" style={{ gridTemplateColumns: SUPPLIER_TABLE_COLUMNS, gap: 8, color: NAVY, opacity: 0.4, backgroundColor: `${NAVY}05` }}>
          <span>Supplier</span><span>Description</span><span>Code</span><span>Pack = 1 order</span><span>{isDraft ? "Pack price" : "Invoice price"}</span><span></span>
        </div>
        {ingredient.sources.length === 0 ? (
          <p className="px-4 py-4 text-xs italic" style={{ color: MUTED }}>No supplier items linked yet — click Add to map one in.</p>
        ) : (
          ingredient.sources.map((s) => (
            <div key={s.supplierProductId} className="grid items-start px-3 py-2.5 text-xs" style={{ gridTemplateColumns: SUPPLIER_TABLE_COLUMNS, gap: 8, borderTop: `1px solid ${LINE}` }}>
              <span className="flex items-center gap-1.5 min-w-0" style={{ minHeight: 28 }}>
                <button type="button" title={s.isPreferred ? "Preferred supplier" : "Set as preferred"} onClick={() => mutators.update({ id: ingredient.id, updates: { preferredSupplierProductId: s.supplierProductId } })} className="flex-shrink-0" style={{ color: s.isPreferred ? GREEN : MUTED }}>
                  <Star size={13} fill={s.isPreferred ? GREEN : "none"} />
                </button>
                <span className="truncate" style={{ color: NAVY, fontSize: 11 }} title={supplierName(s.supplierId)}>{supplierName(s.supplierId)}</span>
              </span>
              <span className="truncate" style={{ color: MUTED, fontSize: 11, lineHeight: "28px" }} title={s.description ?? ""}>{s.description || "—"}</span>
              <span className="truncate tabular-nums" style={{ color: NAVY, fontSize: 11, fontWeight: 600, lineHeight: "28px" }} title={s.productCode}>{s.productCode}</span>
              <PackCell
                source={s}
                ingredientUnit={activeUnit}
                currentPrice={ingredient.currentPrice}
                onSave={(packQuantity, packUnit, unitKey) => mutators.updatePack({ ingredientId: ingredient.id, supplierProductId: s.supplierProductId, packQuantity, packUnit, unitKey })}
                onResolve={(unitKey, packQuantity, packUnit) => mutators.resolveUnit({ ingredientId: ingredient.id, supplierProductId: s.supplierProductId, unitKey, packQuantity, packUnit })}
              />
              <span className="tabular-nums font-semibold text-sm flex items-center" style={{ color: isDraft ? MUTED : NAVY, minHeight: 28 }}>
                {isDraft ? "—" : fmtPrice(s.latestInvoicePrice)}
              </span>
              <button type="button" title="Remove" onClick={() => mutators.detach({ ingredientId: ingredient.id, supplierProductId: s.supplierProductId })} className="justify-self-end flex-shrink-0 flex items-center" style={{ color: WARN, minHeight: 28 }}>
                <X size={14} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Add supplier picker */}
      {adding && (
        <div className="mt-2 p-3 rounded-xl" style={{ backgroundColor: ACCENT_WEAK, border: `1px solid ${ACCENT_LINE}` }}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] font-semibold" style={{ color: NAVY }}>{isDraft ? "Stage a supplier item (saved with ingredient)" : `Map a supplier item into "${ingredient.name}"`}</p>
            <button type="button" onClick={() => { setAdding(false); setAddQuery(""); }} style={{ color: MUTED }}><X size={13} /></button>
          </div>
          <input autoFocus value={addQuery} onChange={(e) => setAddQuery(e.target.value)} placeholder="Type to search supplier items, or pick a suggestion…" className="w-full text-xs px-2.5 py-2 rounded-lg outline-none mb-1.5" style={{ border: `1px solid ${ACCENT_LINE}`, color: NAVY }} />
          <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
            {suggestions.length === 0 ? (
              <p className="text-[11px] italic px-1 py-2" style={{ color: MUTED }}>{addQuery.trim() ? "No matching unmapped supplier items." : "No obvious matches — type to search all unmapped items."}</p>
            ) : (
              suggestions.map(({ u, sc }) => (
                <button key={u.supplierProductId} type="button" disabled={mutators.attachPending}
                  onClick={() => { mutators.attach({ ingredientId: ingredient.id, supplierProductId: u.supplierProductId }); setAdding(false); setAddQuery(""); }}
                  className="text-left text-xs px-2.5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: "white", border: `1px solid ${LINE}` }}>
                  <Link2 size={12} style={{ color: GREEN, flexShrink: 0 }} />
                  <span className="font-semibold tabular-nums" style={{ color: NAVY, width: 84 }}>{u.productCode}</span>
                  <span className="truncate flex-1" style={{ color: NAVY }}>{u.description}</span>
                  <span className="truncate" style={{ color: MUTED, maxWidth: 150 }}>{u.supplierName}</span>
                  {sc > 0 && !addQuery.trim() && <span className="text-[9px] px-1 rounded" style={{ backgroundColor: ACCENT_WEAK, color: GREEN }}>match</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Price history — only for saved ingredients */}
      {!isDraft && (
        <div className="mt-6">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: NAVY, opacity: 0.55 }}>Price history <span className="font-medium normal-case" style={{ color: MUTED }}>(/{ingredient.canonicalUnit}, latest by invoice date)</span></p>
          <PriceHistory ingredientId={ingredient.id} businessId={businessId} unit={ingredient.canonicalUnit} supplierName={supplierName} />
        </div>
      )}
    </div>
  );
}

// ── Group card ────────────────────────────────────────────────────────────────────
function IngredientGroupCard({ name, count, canDelete, onClick, onDelete }: {
  name: string; count: number; canDelete?: boolean; onClick: () => void; onDelete?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const isAll = name === "All";
  const isUngrouped = name === "Ungrouped";
  return (
    <div
      className="relative rounded-2xl transition-shadow"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ border: `1.5px solid ${NAVY}0e`, backgroundColor: isAll ? `${NAVY}04` : "white", boxShadow: hovered ? "0 4px 20px rgba(80,55,30,0.09)" : "none" }}
    >
      {canDelete && hovered && onDelete && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} className="absolute top-3 right-3 rounded-full p-1 z-10" style={{ color: "#c2412e", backgroundColor: "rgba(194,65,46,0.10)" }}>
          <X size={11} />
        </button>
      )}
      <button type="button" onClick={onClick} className="w-full text-left p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: isAll ? `${NAVY}12` : `${NAVY}08` }}>
            {isAll ? <Layers size={18} style={{ color: NAVY, opacity: 0.6 }} /> : <Package size={18} style={{ color: NAVY, opacity: 0.4 }} />}
          </div>
          <span className="text-xs font-bold rounded-full px-2.5 py-1" style={{ backgroundColor: `${NAVY}09`, color: NAVY }}>{count}</span>
        </div>
        <p className="text-sm font-extrabold truncate" style={{ color: NAVY, opacity: isUngrouped ? 0.45 : 1 }}>{name}</p>
        <p className="text-[11px] mt-0.5" style={{ color: NAVY, opacity: 0.4 }}>{count} item{count !== 1 ? "s" : ""}</p>
      </button>
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────────
export default function BaseIngredientTab() {
  const { activeBusiness } = useBusiness();
  const businessId = activeBusiness?.id;
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string>("All");
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [virtualGroups, setVirtualGroups] = useState<string[]>([]);
  const [draftIngredient, setDraftIngredient] = useState<Ingredient | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const newGroupInputRef = useRef<HTMLInputElement>(null);
  const addGroupPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (addingGroup) newGroupInputRef.current?.focus(); }, [addingGroup]);
  useEffect(() => {
    function onOut(e: MouseEvent) {
      if (addGroupPopoverRef.current && !addGroupPopoverRef.current.contains(e.target as Node)) {
        setAddingGroup(false); setNewGroupName("");
      }
    }
    if (addingGroup) document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, [addingGroup]);

  const { data: ingredients, isLoading } = useQuery<Ingredient[]>({
    queryKey: ["ingredients", businessId],
    queryFn: () => fetchIngredients(businessId),
    enabled: Boolean(businessId),
  });
  const { data: unmappedData } = useQuery<UnmappedSupplierProduct[]>({
    queryKey: ["unmapped-products", businessId],
    queryFn: () => fetchUnmappedSupplierProducts(businessId),
    enabled: Boolean(businessId),
  });
  const unmapped: UnmappedSupplierProduct[] = unmappedData ?? [];
  const { data: suppliers } = useQuery<Supplier[]>({
    queryKey: ["suppliers", businessId],
    queryFn: () => fetchSuppliers(businessId),
    enabled: Boolean(businessId),
  });
  const supplierName = (id: string) => suppliers?.find((s: Supplier) => s.id === id)?.name ?? "Unknown supplier";

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["ingredients", businessId] });
    queryClient.invalidateQueries({ queryKey: ["unmapped-products", businessId] });
    queryClient.invalidateQueries({ queryKey: ["ingredient-history"] });
  };

  const updateMut = useMutation({
    mutationFn: (v: { id: string; updates: Parameters<typeof updateIngredient>[1] }) => updateIngredient(v.id, v.updates, businessId),
    onSuccess: invalidate,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteIngredient(id, businessId),
    onSuccess: () => { setSelectedId(null); invalidate(); },
  });
  const attachMut = useMutation({
    mutationFn: (v: { ingredientId: string; supplierProductId: string }) => attachIngredientSource(v.ingredientId, v.supplierProductId, businessId),
    onSuccess: invalidate,
  });
  const detachMut = useMutation({
    mutationFn: (v: { ingredientId: string; supplierProductId: string }) => detachIngredientSource(v.ingredientId, v.supplierProductId, businessId),
    onSuccess: invalidate,
  });
  const updatePackMut = useMutation({
    mutationFn: (v: { ingredientId: string; supplierProductId: string; packQuantity: number | null; packUnit: string | null; unitKey?: string }) =>
      updateIngredientSourcePack(v.ingredientId, v.supplierProductId, { packQuantity: v.packQuantity, packUnit: v.packUnit, unitKey: v.unitKey }, businessId),
    onSuccess: invalidate,
  });
  const resolveUnitMut = useMutation({
    mutationFn: (v: { ingredientId: string; supplierProductId: string; unitKey: string; packQuantity: number; packUnit: string }) =>
      resolveUnitChange(v.ingredientId, v.supplierProductId, { unitKey: v.unitKey, action: "map", packQuantity: v.packQuantity, packUnit: v.packUnit }, businessId),
    onSuccess: invalidate,
  });

  // ── Local draft management ────────────────────────────────────────────────────
  function startNewIngredient(category: string | null) {
    setDraftIngredient(newDraftIngredient(category));
    setSelectedId(DRAFT_ID);
  }

  function cancelDraft() {
    setDraftIngredient(null);
    setSelectedId(null);
  }

  function addLocalSource(supplierProductId: string) {
    const product = unmapped.find((u) => u.supplierProductId === supplierProductId);
    if (!product) return;
    setDraftIngredient((prev) => {
      if (!prev) return null;
      const isFirst = prev.sources.length === 0;
      const newSource: IngredientSource = {
        supplierProductId: product.supplierProductId,
        supplierId: product.supplierId,
        productCode: product.productCode,
        description: product.description,
        packQuantity: product.packQuantity,
        packUnit: product.packUnit,
        packUnits: [],
        pendingUnits: [],
        observedUnits: [],
        latestPrice: null,
        latestInvoicePrice: null,
        latestDate: null,
        isPreferred: isFirst,
      };
      return {
        ...prev,
        sources: [...prev.sources, newSource],
        sourceCount: prev.sources.length + 1,
        preferredSupplierProductId: isFirst ? product.supplierProductId : prev.preferredSupplierProductId,
      };
    });
  }

  function removeLocalSource(supplierProductId: string) {
    setDraftIngredient((prev) => {
      if (!prev) return null;
      const newSources = prev.sources.filter((s) => s.supplierProductId !== supplierProductId);
      return { ...prev, sources: newSources, sourceCount: newSources.length };
    });
  }

  function updateLocalSourcePack(supplierProductId: string, packQuantity: number | null, packUnit: string | null) {
    setDraftIngredient((prev) => {
      if (!prev) return null;
      return { ...prev, sources: prev.sources.map((s) => s.supplierProductId === supplierProductId ? { ...s, packQuantity, packUnit } : s) };
    });
  }

  function updateLocalDraft(updates: Parameters<typeof updateIngredient>[1]) {
    setDraftIngredient((prev) => {
      if (!prev) return null;
      const prefId = "preferredSupplierProductId" in updates
        ? (updates.preferredSupplierProductId ?? null)
        : prev.preferredSupplierProductId;
      return {
        ...prev,
        ...updates,
        sources: prev.sources.map((s) => ({ ...s, isPreferred: s.supplierProductId === prefId })),
      };
    });
  }

  async function saveDraft(data: { name: string; canonicalUnit: string; category: string | null; manualPrice: number | null }) {
    if (isSavingDraft) return;
    setIsSavingDraft(true);
    try {
      const sources = draftIngredient?.sources ?? [];
      const created = await createIngredient({ name: data.name, canonicalUnit: data.canonicalUnit, category: data.category }, businessId) as { id?: string };
      if (!created?.id) return;

      if (data.manualPrice != null) {
        await updateIngredient(created.id, { manualPrice: data.manualPrice }, businessId);
      }

      for (const source of sources) {
        await attachIngredientSource(created.id, source.supplierProductId, businessId);
        if (source.packQuantity != null || source.packUnit != null) {
          await updateIngredientSourcePack(created.id, source.supplierProductId, { packQuantity: source.packQuantity, packUnit: source.packUnit }, businessId);
        }
      }

      setDraftIngredient(null);
      setSelectedId(created.id);
      invalidate();
    } catch (e) {
      console.error("Failed to save ingredient:", e);
    } finally {
      setIsSavingDraft(false);
    }
  }

  const isDraftSelected = selectedId === DRAFT_ID;

  // ── Groups ────────────────────────────────────────────────────────────────────
  const list: Ingredient[] = ingredients ?? [];

  const dataGroups = useMemo(
    () => [...new Set(list.map((i) => i.category).filter((c): c is string => Boolean(c)))].sort(),
    [list],
  );
  const groups = useMemo(
    () => [...new Set([...dataGroups, ...virtualGroups.filter((g) => !dataGroups.includes(g))])].sort(),
    [dataGroups, virtualGroups],
  );
  useEffect(() => { setVirtualGroups((prev) => prev.filter((g) => !dataGroups.includes(g))); }, [dataGroups]);

  function addGroup(name: string) {
    const trimmed = name.trim();
    if (!trimmed || groups.includes(trimmed)) return;
    setVirtualGroups((prev) => [...prev, trimmed]);
  }

  const groupCardData = useMemo(() => {
    const cards: { name: string; count: number }[] = [{ name: "All", count: list.length }];
    for (const g of groups) cards.push({ name: g, count: list.filter((i) => i.category === g).length });
    const ungrouped = list.filter((i) => !i.category || !groups.includes(i.category)).length;
    if (ungrouped > 0) cards.push({ name: "Ungrouped", count: ungrouped });
    return cards;
  }, [list, groups]);

  const groupItems = selectedGroup === "All" ? list
    : selectedGroup === "Ungrouped" ? list.filter((i) => !i.category || !groups.includes(i.category))
    : list.filter((i) => i.category === selectedGroup);

  const filteredItems = search.trim()
    ? groupItems.filter((i) => i.name.toLowerCase().includes(search.toLowerCase()) || i.sources.some((s) => (s.productCode || "").toLowerCase().includes(search.toLowerCase())))
    : groupItems;

  const displayItems: Ingredient[] = draftIngredient ? [draftIngredient, ...filteredItems] : filteredItems;
  const selected = isDraftSelected ? draftIngredient : list.find((i) => i.id === selectedId) ?? null;
  const defaultCategory = selectedGroup !== "All" && selectedGroup !== "Ungrouped" ? selectedGroup : null;

  // ── Mutators (draft-aware) ────────────────────────────────────────────────────
  const mutators = {
    update: isDraftSelected
      ? (v: { id: string; updates: Parameters<typeof updateIngredient>[1] }) => updateLocalDraft(v.updates)
      : updateMut.mutate,
    remove: isDraftSelected ? () => cancelDraft() : deleteMut.mutate,
    attach: isDraftSelected
      ? (v: { ingredientId: string; supplierProductId: string }) => addLocalSource(v.supplierProductId)
      : attachMut.mutate,
    detach: isDraftSelected
      ? (v: { ingredientId: string; supplierProductId: string }) => removeLocalSource(v.supplierProductId)
      : detachMut.mutate,
    updatePack: isDraftSelected
      ? (v: { ingredientId: string; supplierProductId: string; packQuantity: number | null; packUnit: string | null; unitKey?: string }) => updateLocalSourcePack(v.supplierProductId, v.packQuantity, v.packUnit)
      : updatePackMut.mutate,
    resolveUnit: resolveUnitMut.mutate,
    attachPending: isDraftSelected ? false : attachMut.isPending,
    saveDraft,
    saveDraftPending: isSavingDraft,
    cancelDraft,
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden" style={{ background: "#fff" }}>
      <div className="flex-1 min-h-0 px-6 pb-6 pt-4 flex gap-4 overflow-hidden">
        {/* Left panel */}
        <div style={{ width: 330, flexShrink: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          <div style={{ padding: "13px 16px", borderBottom: `1px solid ${LINE}`, background: RAISED, display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, height: 36, padding: "0 13px", background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 999 }}>
              <Search size={15} style={{ color: MUTED, flexShrink: 0 }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ingredients…"
                className="outline-none bg-transparent flex-1" style={{ fontSize: 13, color: NAVY }} />
              {search && <button type="button" onClick={() => setSearch("")} style={{ color: MUTED, background: "none", border: "none", cursor: "pointer", padding: 0 }}><X size={12} /></button>}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
                <select
                  value={selectedGroup}
                  onChange={(e) => { setSelectedGroup(e.target.value); setSearch(""); setSelectedId(null); setDraftIngredient(null); }}
                  aria-label="Ingredient category"
                  style={{ width: "100%", height: 38, borderRadius: 999, border: `1px solid ${LINE}`, background: SURFACE, color: NAVY, fontSize: 12.5, fontWeight: 700, outline: "none", padding: "0 38px 0 13px", boxShadow: "0 1px 2px rgba(80,55,30,0.05)", cursor: "pointer", appearance: "none", WebkitAppearance: "none" }}
                >
                  {groupCardData.map((g) => <option key={g.name} value={g.name}>{g.name} ({g.count})</option>)}
                </select>
                <ChevronDown size={14} style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: MUTED, pointerEvents: "none" }} />
              </div>
              <div ref={addGroupPopoverRef} style={{ position: "relative", flexShrink: 0 }}>
                <button data-menu-costing-action type="button" onClick={() => setAddingGroup((v) => !v)} title="Add a group" aria-label="Add a group"
                  style={{ height: 38, minWidth: 112, borderRadius: 999, cursor: "pointer", background: SURFACE, border: `1px solid ${LINE}`, color: NAVY, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "0 14px", fontSize: 12, fontWeight: 800, boxShadow: "0 1px 2px rgba(80,55,30,0.05)", flexShrink: 0 }}>
                  <Plus size={13} strokeWidth={2.4} />
                  <span>Add a group</span>
                </button>
                {addingGroup && (
                  <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30, background: SURFACE, border: `1px solid ${NAVY}14`, borderRadius: 12, padding: 12, minWidth: 200, boxShadow: "0 8px 24px rgba(80,55,30,.12)" }}>
                    <p style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", color: NAVY, opacity: 0.4, marginBottom: 8 }}>New group</p>
                    <input
                      ref={newGroupInputRef}
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="Group name…"
                      className="w-full outline-none"
                      style={{ border: `1.5px solid ${NAVY}24`, borderRadius: 8, padding: "6px 10px", fontSize: 12, color: NAVY, marginBottom: 8 }}
                      onFocus={(e) => (e.target.style.borderColor = GREEN)}
                      onBlur={(e) => (e.target.style.borderColor = `${NAVY}24`)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { const t = newGroupName.trim(); if (t) addGroup(t); setAddingGroup(false); setNewGroupName(""); }
                        if (e.key === "Escape") { setAddingGroup(false); setNewGroupName(""); }
                      }}
                    />
                    <button
                      data-menu-costing-action
                      type="button"
                      onClick={() => { const t = newGroupName.trim(); if (t) addGroup(t); setAddingGroup(false); setNewGroupName(""); }}
                      style={{ width: "100%", background: NAVY, color: "#fff", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                    >
                      Create group
                    </button>
                  </div>
                )}
              </div>
            </div>

          </div>

          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {isLoading ? (
              <div style={{ padding: "48px 16px", textAlign: "center" }}><Loader2 size={20} className="mx-auto animate-spin" style={{ color: MUTED }} /></div>
            ) : displayItems.length === 0 ? (
              <div style={{ padding: "48px 16px", textAlign: "center" }}>
                <Package size={28} style={{ margin: "0 auto 8px", color: NAVY, opacity: 0.15 }} />
                <p style={{ fontSize: 12, color: MUTED }}>{search ? "No match." : "No ingredients here yet."}</p>
              </div>
            ) : (
              displayItems.map((ing) => {
                const sel = ing.id === selectedId;
                const isDraftRow = ing.id === DRAFT_ID;
                return (
                  <button
                    key={ing.id}
                    type="button"
                    onClick={() => { if (!isDraftRow) setDraftIngredient(null); setSelectedId(ing.id); }}
                    style={{ width: "100%", textAlign: "left", padding: "11px 16px", background: sel ? ACCENT_WEAK : "transparent", borderLeft: `3px solid ${sel ? GREEN : "transparent"}`, borderBottom: `1px solid ${LINE}`, display: "flex", flexDirection: "column", gap: 3, cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                      <span style={{ fontSize: 13.5, fontWeight: 600, color: isDraftRow ? MUTED : NAVY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontStyle: isDraftRow ? "italic" : "normal" }}>
                        {isDraftRow ? (ing.name || "New ingredient") : ing.name}
                      </span>
                      {isDraftRow ? (
                        <span style={{ fontSize: 10, fontWeight: 600, color: MUTED, flexShrink: 0, backgroundColor: `${NAVY}08`, borderRadius: 4, padding: "1px 5px" }}>unsaved</span>
                      ) : (
                        <span style={{ fontSize: 12, fontWeight: 700, color: GREEN, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{fmtPrice(ing.currentPrice)}<span style={{ color: MUTED, fontWeight: 500 }}>/{ing.canonicalUnit}</span></span>
                      )}
                    </div>
                    {!isDraftRow && <span style={{ fontSize: 11, color: MUTED }}>{ing.category || "Ungrouped"}</span>}
                  </button>
                );
              })
            )}
          </div>

          <div style={{ padding: 13, borderTop: `1px solid ${LINE}`, flexShrink: 0 }}>
            <button type="button" onClick={() => startNewIngredient(defaultCategory)}
              className="w-full inline-flex items-center justify-center gap-2"
              style={{ height: 36, borderRadius: 999, border: `1px dashed ${LINE}`, background: "transparent", color: MUTED, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
              onMouseEnter={(e) => { const b = e.currentTarget; b.style.color = GREEN; b.style.borderColor = ACCENT_LINE; }}
              onMouseLeave={(e) => { const b = e.currentTarget; b.style.color = MUTED; b.style.borderColor = LINE; }}>
              <Plus size={13} /> New ingredient
            </button>
          </div>
        </div>

        {/* Right panel */}
        <div style={{ flex: 1, minWidth: 0, background: SURFACE, border: `1px solid ${LINE}`, borderRadius: 14, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)" }}>
          {selected ? (
            <BaseIngredientEditor
              ingredient={selected}
              businessId={businessId}
              unmapped={unmapped}
              supplierName={supplierName}
              groups={groups}
              isDraft={isDraftSelected}
              mutators={mutators}
            />
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 32 }}>
              <div style={{ width: 56, height: 56, borderRadius: 18, background: `${NAVY}07`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <Package size={24} style={{ color: NAVY, opacity: 0.3 }} />
              </div>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Base Ingredients</p>
              <p style={{ fontSize: 12, color: MUTED, maxWidth: 260, lineHeight: 1.5 }}>Select an ingredient to view or edit, or create a new one.</p>
              <button
                data-menu-costing-action
                type="button"
                onClick={() => startNewIngredient(defaultCategory)}
                style={{ marginTop: 20, height: 38, padding: "0 20px", borderRadius: 999, background: GREEN, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <Plus size={14} /> New ingredient
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
